import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import totalLocationData from "../../../../../../data/locationdata";
import styles from "./mealplanimage.module.css";
import MealPlanPreviewModal, { DownloadIcon } from "./mealplanpreviewmodal";
import { FOOD_NAMES, FOOD_SRC, loadPretendard } from "./mealplanassets";
import { blobOf, drawBanner, drawBody, drawThumb, preloadFood } from "./mealplandraw";
import {
    DAYS, WEEK_COLORS, ImageKey, ORDER_K, NAMES, LABEL, RegData, SpData,
    addDays, autoPick, buildWeekView, dateKey, fileBase, fmt, mondayOf, weekInfo
} from "./mealplanutil";

//본사직원페이지 > 식단표 이미지 만들기
//주차별 정기식(regularMealList)·특식(selectedMeal+meal)을 표로 보여주고 블로그용 이미지 4장을 브라우저에서 만들어 PNG 로 저장
//식단·휴무 수정은 도시락신청 메뉴에서. 여기서 저장하는 건 썸네일 음식·크기뿐 (정기식 지점 + 주차 기준)

const API = "/dashboard/mealplanimage";
const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };
const DEFAULT_LOCATION = "songpa";
const ORDINALS = ["첫번째", "두번째", "세번째", "네번째", "다섯번째", "여섯번째", "일곱번째", "여덟번째"];
const PREVIEW_H = 180;

const MealPlanImage: React.FC<any> = (props) => {

    const academy = props.user ? props.user.academy : "sunbae";
    const regBranches = useMemo(() => totalLocationData.filter((each) => each.academy.includes(academy) && each.needRegularMeal), [academy]);
    const spBranches = useMemo(() => totalLocationData.filter((each) => each.academy.includes(academy) && each.english !== "total"), [academy]);

    const [monday, setMonday] = useState<Date>(() => mondayOf(new Date()));
    const [tab, setTab] = useState<"reg" | "sp">("reg");
    const [regLoc, setRegLoc] = useState(DEFAULT_LOCATION);
    const [spLoc, setSpLoc] = useState(DEFAULT_LOCATION);

    const [loading, setLoading] = useState(true);
    const [reg, setReg] = useState<RegData | null>(null);
    const [sp, setSp] = useState<SpData | null>(null);
    const [emoji, setEmoji] = useState<string | null>(null);
    const [scale, setScale] = useState(100);
    const [spPick, setSpPick] = useState<{ [slot: string]: number }>({});   //특식 칸별 선택(저장 안 함)

    const [images, setImages] = useState<{ [k: string]: HTMLCanvasElement }>({});
    const [pickerOpen, setPickerOpen] = useState(false);
    const [modalKey, setModalKey] = useState<ImageKey>("thumb");
    const [modalOpen, setModalOpen] = useState(false);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const previewRefs = useRef<{ [k: string]: HTMLCanvasElement | null }>({});
    const loadSeq = useRef(0);
    const renderSeq = useRef(0);
    const saveTimer = useRef<any>(null);

    const regName = (regBranches.find((e) => e.english === regLoc) || { korean: regLoc }).korean;
    const spName = (spBranches.find((e) => e.english === spLoc) || { korean: spLoc }).korean;
    const mondayKey = dateKey(monday);
    const { month, week } = weekInfo(monday);
    const saturday = addDays(monday, 5);
    const both = !!(reg && sp);

    useEffect(() => {
        loadPretendard();
        preloadFood();
    }, []);

    //주차·지점 바뀌면 다시 불러오기 (늦게 온 응답은 버림)
    useEffect(() => {
        const seq = ++loadSeq.current;
        setLoading(true);
        setSpPick({});
        (async () => {
            try {
                const result: any = await getFetch(`${API}/week?monday=${mondayKey}&regLocation=${regLoc}&spLocation=${spLoc}`);
                if (seq !== loadSeq.current) return;
                if (result.message === "success") {
                    setReg(result.reg);
                    setSp(result.sp);
                    setEmoji(result.setting ? result.setting.emoji : null);
                    setScale(result.setting ? result.setting.scale : 100);
                } else {
                    setReg(null);
                    setSp(null);
                    toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (본사직원 전용)" : "식단을 불러오지 못했어요.", toastOptions);
                }
            } catch (e) {
                console.log(e);
                if (seq !== loadSeq.current) return;
                setReg(null);
                setSp(null);
                toast.error("식단을 불러오지 못했어요.", toastOptions);
            } finally {
                if (seq === loadSeq.current) setLoading(false);
            }
        })();
    }, [mondayKey, regLoc, spLoc]);

    const ap = useMemo(() => autoPick(reg, sp), [reg, sp]);
    const foodName = emoji || ap.name || "볶음밥";
    const view = useMemo(() => buildWeekView(monday, reg, sp, spPick, emoji, scale), [monday, reg, sp, spPick, emoji, scale]);

    //이미지 4장 다시 그리기 — 슬라이더 연속 입력은 150ms 묶어서, 늦게 끝난 이전 렌더는 버림
    useEffect(() => {
        if (loading || !both) {
            renderSeq.current++;
            setImages({});
            return;
        }
        const seq = ++renderSeq.current;
        const timer = setTimeout(async () => {
            try {
                await loadPretendard();
                try { await (document as any).fonts.ready; } catch (e) { }
                const [t, bn, r, s] = await Promise.all([drawThumb(view, foodName), drawBanner(view, foodName), drawBody(view, "reg"), drawBody(view, "sp")]);
                if (seq !== renderSeq.current) return;
                setImages({ thumb: t, banner: bn, reg: r, sp: s });
            } catch (e) {
                console.log(e);
                if (seq === renderSeq.current) toast.error("이미지를 만들지 못했어요.", toastOptions);
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [view, foodName, loading, both]);

    //미리보기 캔버스(높이 180)에 축소해서 그림
    useEffect(() => {
        ORDER_K.forEach((k) => {
            const m = previewRefs.current[k];
            const src = images[k];
            if (!m) return;
            if (!src) { m.width = 1; m.height = 1; return; }
            const w = Math.round(PREVIEW_H * src.width / src.height);
            m.width = w; m.height = PREVIEW_H;
            m.getContext("2d")!.drawImage(src, 0, 0, w, PREVIEW_H);
        });
    }, [images]);

    useEffect(() => {
        if (!both) { setPickerOpen(false); setModalOpen(false); }
    }, [both]);

    //썸네일 음식·크기 저장 (정기식 지점 + 주차)
    const saveSetting = async (location: string, mondayStr: string, nextEmoji: string | null, nextScale: number) => {
        try {
            const result: any = await postFetch(`${API}/thumbnail`, { monday: mondayStr, location, emoji: nextEmoji, scale: nextScale });
            if (result.message === "success") {
                toast.success("썸네일 음식을 저장했어요.", toastOptions);
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (본사직원 전용)" : "썸네일 음식을 저장하지 못했어요.", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("썸네일 음식을 저장하지 못했어요.", toastOptions);
        }
    };

    const scheduleSave = (nextEmoji: string | null, nextScale: number, delay: number) => {
        clearTimeout(saveTimer.current);
        const location = regLoc, mondayStr = mondayKey;
        saveTimer.current = setTimeout(() => saveSetting(location, mondayStr, nextEmoji, nextScale), delay);
    };

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const pickEmoji = (name: string | null) => {
        setEmoji(name);
        scheduleSave(name, scale, 0);
    };

    const changeScale = (v: number) => {
        setScale(v);
        scheduleSave(emoji, v, 500);
    };

    //저장 — a[download] 로 바로 내려받기
    const offer = (filename: string, blob: Blob | null) => {
        if (!blob) throw new Error("NO_BLOB");
        const u = URL.createObjectURL(blob), a = document.createElement("a");
        a.href = u; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 4000);
    };

    const base = fileBase(regName, spName, month, week);

    const downloadOne = async (k: ImageKey) => {
        const cv = images[k];
        if (!cv) return;
        const filename = `${base}_${NAMES[k]}.png`;
        try {
            offer(filename, await blobOf(cv));
            toast.success(`${filename} 다운로드를 시작했어요.`, toastOptions);
        } catch (e) {
            console.log(e);
            toast.error("저장하지 못했어요. 미리보기 이미지를 우클릭해서 저장하세요.", toastOptions);
        }
    };

    //4장 한 번에 — ZIP 없이 PNG 4장을 400ms 간격으로 차례로 내려받기
    const downloadAll = async () => {
        if (!both || busyKey) return;
        setBusyKey("all");
        try {
            for (let i = 0; i < ORDER_K.length; i++) {
                const k = ORDER_K[i];
                if (!images[k]) throw new Error("NO_IMAGE");
                offer(`${base}_${NAMES[k]}.png`, await blobOf(images[k]));
                if (i < ORDER_K.length - 1) await new Promise((r) => setTimeout(r, 400));
            }
            toast.success("4장 다운로드를 시작했어요. 여러 파일 다운로드 허용을 물으면 '허용'을 눌러주세요.", { ...toastOptions, autoClose: 5000 });
        } catch (e) {
            console.log(e);
            toast.error("저장하지 못했어요. 개별 PNG 버튼으로 다시 시도해주세요.", toastOptions);
        } finally {
            setBusyKey(null);
        }
    };

    const openModal = (k: ImageKey) => {
        if (!images[k]) return;
        setModalKey(k);
        setModalOpen(true);
    };

    //---------- 표 ----------
    const hol = view.hol[tab];
    const loaded = tab === "reg" ? !!reg : !!sp;
    const tabBranchName = tab === "reg" ? regName : spName;
    const tabData: any = tab === "reg" ? reg : sp;

    const renderSpCell = (m: "lunch" | "dinner", i: number) => {
        const list = (sp && sp[m][i]) || [];
        if (!list.length) return <td key={i} className={styles.emptyCell}>—</td>;
        const pick = Math.min(spPick[`${i}-${m}`] || 0, list.length - 1);
        const c = list[pick];
        return (
            <td key={i}>
                <div className={styles.sp}>
                    <span className={styles.store}>{c.store}</span>
                    <span className={styles.name}>{c.menu}</span>
                    {list.length > 1 &&
                        <div className={styles.optrow}>
                            {list.map((_, idx) => idx === pick ? null : (
                                <button key={idx} type="button" className={styles.opt}
                                    title={`${list[idx].store} ${list[idx].menu}`}
                                    onClick={() => setSpPick((prev) => ({ ...prev, [`${i}-${m}`]: idx }))}>
                                    {`${ORDINALS[idx] || `${idx + 1}번째`}메뉴`}
                                </button>
                            ))}
                        </div>
                    }
                </div>
            </td>
        );
    };

    const renderRegCell = (m: "lunch" | "dinner", i: number) => {
        const items = (reg && reg.days[i] && reg.days[i][m]) || [];
        if (!items.length) return <td key={i} className={styles.emptyCell}>—</td>;
        return (
            <td key={i}>
                <ul className={styles.menu}>
                    {items.map((x, j) => (
                        <li key={j} className={j === 0 ? styles.main : (items.length > 2 && j >= items.length - 2 ? styles.side : "")}>{x}</li>
                    ))}
                </ul>
            </td>
        );
    };

    const ROWS: ["lunch" | "dinner", string, string, string][] = [["lunch", "중식", "☀️", "11:50 – 13:00"], ["dinner", "석식", "🌙", "17:50 – 19:00"]];

    const holNames = [
        ...view.hol.reg.map((h, i) => (h ? `정기식 ${DAYS[i]}` : null)),
        ...view.hol.sp.map((h, i) => (h ? `특식 ${DAYS[i]}` : null))
    ].filter(Boolean);

    const statusText = both ? "정기식·특식 모두 등록됨" : (reg || sp ? "한쪽만 등록됨" : "미등록");
    const subtitle = `${regName === spName ? regName : `${regName}(정기)·${spName}(특식)`} ${month}월 ${week}주차`;

    return (
        <div className={styles.root}>
            <header className={styles.head}>
                <div>
                    <h1>식단표 이미지 만들기</h1>
                    <p>주차별 정기식·특식 식단을 확인하고 블로그 업로드용 이미지를 내려받습니다.</p>
                </div>
                <div className={styles.toolbar}>
                    <div className={styles.weekpick}>
                        <button className={styles.ibtn} type="button" aria-label="이전 주차" onClick={() => setMonday(addDays(monday, -7))}>‹</button>
                        <div className={styles.weekLabel}>
                            <span className={styles.dot} style={{ background: WEEK_COLORS[week] || WEEK_COLORS[1] }}></span>
                            <span>{`${month}월 ${week}주차`}</span>
                        </div>
                        <button className={styles.ibtn} type="button" aria-label="다음 주차" onClick={() => setMonday(addDays(monday, 7))}>›</button>
                        <span className={styles.range}>{`${fmt(monday)} (월) – ${fmt(saturday)} (토)`}</span>
                    </div>
                    <button className={styles.ibtn} type="button" title="이번 주로" aria-label="이번 주로" onClick={() => setMonday(mondayOf(new Date()))}>⌂</button>
                </div>
            </header>

            <div className={styles.grid}>
                <section className={styles.card}>
                    <div className={styles.tabs} role="tablist">
                        <button className={styles.tab} role="tab" type="button" aria-selected={tab === "reg"} onClick={() => setTab("reg")}>정기식</button>
                        <button className={styles.tab} role="tab" type="button" aria-selected={tab === "sp"} onClick={() => setTab("sp")}>특식</button>
                        <div className={styles.bsel}>
                            <label className={styles.bl}>
                                <span>정기식 지점</span>
                                <select className={styles.sel} aria-label="정기식 지점" value={regLoc} onChange={(e) => setRegLoc(e.target.value)}>
                                    {regBranches.map((b) => <option key={b.english} value={b.english}>{b.korean}</option>)}
                                </select>
                            </label>
                            <label className={styles.bl}>
                                <span>특식 지점</span>
                                <select className={styles.sel} aria-label="특식 지점" value={spLoc} onChange={(e) => setSpLoc(e.target.value)}>
                                    {spBranches.map((b) => <option key={b.english} value={b.english}>{b.korean}</option>)}
                                </select>
                            </label>
                        </div>
                    </div>
                    <div className={styles.meta}>
                        <div>
                            <b>{`${tabBranchName} ${tab === "reg" ? "정기식" : "특식"}${tab === "reg" && reg && reg.isShared ? " (공통 식단)" : ""}`}</b>
                            {" · 등록 "}<b>{(tabData && tabData.registeredAt) || "—"}</b>
                            {" · 담당 "}<b>{(tabData && tabData.registeredBy) || "—"}</b>
                        </div>
                        {!loading &&
                            <span className={`${styles.status} ${both ? styles.ok : styles.warn}`}><i></i>{statusText}</span>
                        }
                    </div>
                    <div className={styles.tblwrap}>
                        <table className={styles.table} aria-label="주간 식단표">
                            <thead>
                                <tr>
                                    <th className={styles.rowh}></th>
                                    {DAYS.map((x, i) => (
                                        <th key={i} className={`${i === 5 ? styles.sat : ""} ${loaded && hol[i] ? styles.hol : ""}`}>
                                            {x}<span className={styles.d}>{fmt(addDays(monday, i))}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ?
                                    <tr><td colSpan={7} className={styles.emptyRow}>불러오는 중…</td></tr>
                                    : !loaded ?
                                        <tr>
                                            <td colSpan={7} className={styles.emptyRow}>
                                                {`${tabBranchName} ${month}월 ${week}주차 ${tab === "reg" ? "정기식" : "특식"} 식단이 아직 등록되지 않았습니다.`}<br />
                                                <span style={{ fontSize: 12 }}>{`도시락신청 메뉴의 ${tab === "reg" ? "정기식 메뉴 넣기" : "특식 신청하기"}에서 등록하면 여기에 표시됩니다.`}</span>
                                            </td>
                                        </tr>
                                        :
                                        ROWS.map(([k, label, ic, time]) => (
                                            <tr key={k}>
                                                <td className={styles.rowh}>
                                                    <span className={styles.ic}>{ic}</span>
                                                    <span className={styles.t}>{label}</span>
                                                    <span className={styles.s}>{time}</span>
                                                </td>
                                                {DAYS.map((_, i) => {
                                                    if (hol[i]) return <td key={i}><div className={styles.holCell}>{view.holText[tab][i]}</div></td>;
                                                    return tab === "reg" ? renderRegCell(k, i) : renderSpCell(k, i);
                                                })}
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                    </div>
                </section>

                <aside className={styles.panel}>
                    <div className={styles.card}>
                        <div className={styles.ph}><span className={styles.i}>▣</span>이미지 다운로드</div>
                        <div className={styles.pb}>
                            <p className={styles.desc}>블로그·카페 업로드용 4장이 이 주차 식단으로 자동 생성됩니다. 미리보기를 누르면 실제 크기로 볼 수 있습니다.</p>
                            <div className={styles.previews}>
                                {ORDER_K.map((k) => (
                                    <button key={k} className={styles.pv} type="button" disabled={!images[k]} onClick={() => openModal(k)}>
                                        <div className={styles.img}><canvas ref={(el) => { previewRefs.current[k] = el; }}></canvas></div>
                                        <div className={styles.cap}>
                                            {LABEL[k]}
                                            <small>{images[k] ? `${images[k].width}×${images[k].height}` : (k === "thumb" ? "1080×1080" : k === "banner" ? "1080×752" : "—")}</small>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <button className={`${styles.btn} ${styles.primary} ${busyKey === "all" ? styles.busy : ""}`} type="button"
                                disabled={!both || !images.thumb} onClick={downloadAll}>
                                <DownloadIcon size={16} />
                                <span>{busyKey === "all" ? "저장하는 중…" : "4장 한 번에 저장"}</span>
                            </button>
                            <div className={styles.btnrow}>
                                {ORDER_K.map((k) => (
                                    <button key={k} className={`${styles.btn} ${styles.sm}`} type="button" disabled={!both || !images[k]} onClick={() => downloadOne(k)}>
                                        {`${LABEL[k]} PNG`}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.divider}></div>
                            <div className={styles.kv}><span>휴무</span><b>{holNames.length ? holNames.join(", ") : "없음"}</b></div>
                            <button className={`${styles.kv} ${styles.kvbtn}`} type="button" aria-haspopup="dialog" disabled={!both}
                                onClick={() => setPickerOpen(!pickerOpen)}>
                                <span>썸네일 음식</span>
                                <b>{!both ? "—" : (emoji ? `${emoji} (직접 선택)` : `${ap.name} (자동)`)}</b>
                                <span className={styles.chev}>{pickerOpen ? "⌄" : "›"}</span>
                            </button>
                            {pickerOpen && both &&
                                <div className={styles.picker}>
                                    <div className={styles.pkHead}>
                                        <b>썸네일 음식 바꾸기</b>
                                        <button className={styles.pkAuto} type="button" onClick={() => pickEmoji(null)}>자동 추천으로</button>
                                    </div>
                                    <div className={styles.pkGrid}>
                                        {FOOD_NAMES.map((n) => (
                                            <button key={n} type="button" className={styles.em} aria-pressed={n === foodName} onClick={() => pickEmoji(n)}>
                                                <img src={FOOD_SRC[n]} alt="" /><span>{n}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <label className={styles.pkSize}>
                                        <span>음식 크기</span>
                                        <input type="range" min={70} max={130} step={1} value={scale} onChange={(e) => changeScale(+e.target.value)} />
                                        <b>{`${scale}%`}</b>
                                    </label>
                                    <p className={styles.pkHint}>
                                        {emoji
                                            ? `직접 고른 '${emoji}'가 썸네일·본문 상단에 들어갑니다. (${regName} ${month}월 ${week}주차에 저장)`
                                            : `자동 추천: ${ap.from} → '${ap.name}'. 눌러서 바꿀 수 있습니다.`}
                                    </p>
                                </div>
                            }
                            <div className={styles.kv}><span>데이터 출처</span><b>regularMealList / selectedMeal</b></div>
                        </div>
                    </div>
                    <p className={styles.foot}>식단 내용·휴무 수정은 <b>도시락신청</b> 메뉴(정기식 메뉴 넣기 · 특식 신청하기)에서 합니다. 썸네일 음식과 크기는 여기서 바로 바꿀 수 있습니다.</p>
                </aside>
            </div>

            <MealPlanPreviewModal
                open={modalOpen}
                imageKey={modalKey}
                images={images}
                subtitle={subtitle}
                onChange={(k) => setModalKey(k)}
                onClose={() => setModalOpen(false)}
                onDownload={downloadOne}
            />
        </div>
    );
};

export default MealPlanImage;
